import { colors, createTheme, Theme } from '@mui/material';
import { green, grey, indigo } from '@mui/material/colors';
import React from 'react';
import { getUiThemeStore } from './logic/ui-theme';

// Design tokens for the new Projects page design
export const designTokens = {
  colors: {
    bg: {
      primary: '#0a0a0a',
      card: '#111',
      cardHover: '#151515',
      elevated: '#1a1a1a',
      buttonHover: '#222',
    },
    text: {
      primary: '#fff',
      secondary: '#888',
      muted: '#666',
      dim: '#444',
      faint: '#333',
    },
    accent: {
      primary: '#ff3c3c',
      primaryHover: '#ff5555',
      blue: '#3b82f6',
      blueLight: '#60a5fa',
      blueLighter: '#93c5fd',
    },
    status: {
      active: '#22c55e',
      activeHover: '#16a34a',
      activeBg: 'rgba(34, 197, 94, 0.15)',
      activeBorder: 'rgba(34, 197, 94, 0.3)',
      draft: '#eab308',
      draftBg: 'rgba(234, 179, 8, 0.15)',
      draftBorder: 'rgba(234, 179, 8, 0.3)',
      paused: '#ef4444',
      pausedBg: 'rgba(239, 68, 68, 0.15)',
      archived: '#666',
      archivedBg: '#1a1a1a',
    },
    border: {
      subtle: '#1a1a1a',
      default: '#222',
      hover: '#333',
      focus: '#ff3c3c',
    },
    // Destructive actions (delete, remove, etc.)
    destructive: {
      default: '#f44336',
      hover: '#d32f2f',
      bg: 'rgba(244, 67, 54, 0.1)',
    },
    // Connection states (teal for connect, red for disconnect)
    connection: {
      teal: '#2dd4bf',
      tealBg: 'rgba(45, 212, 191, 0.15)',
      tealBorder: 'rgba(45, 212, 191, 0.35)',
      tealHover: 'rgba(45, 212, 191, 0.25)',
      red: '#ff3c3c',
      redBg: 'rgba(255, 60, 60, 0.15)',
      borderDefault: '#333',
    },
    // AI-generated content indicators (Quick-Start, etc.)
    ai: {
      start: '#2dd4bf', // teal - gradient start
      end: '#a78bfa', // purple - gradient end
      glow: 'rgba(45, 212, 191, 0.4)', // ambient glow
      badge: 'rgba(45, 212, 191, 0.15)', // badge background
      badgeBorder: 'rgba(45, 212, 191, 0.3)', // badge border
    },
  },
  typography: {
    // Dynamic fonts - reads from selected font theme
    get fontMono() {
      return getUiThemeStore().selectedFontTheme.mono;
    },
    get fontEditorial() {
      return getUiThemeStore().selectedFontTheme.editorial;
    },
    // Type scale based on Celine Nguyen's UX research: ~4 sizes, 2 weights
    fontSize: {
      xs: 11, // Badges, tiny labels, status pills
      sm: 13, // Metadata, secondary text, descriptions
      base: 16, // Body text, primary UI elements
      lg: 24, // Headers, card titles, section headers
    },
    fontWeight: {
      normal: 400, // Body text, descriptions
      bold: 600, // Emphasis, headers, titles
    },
  },
  spacing: {
    radiusXs: '4px',
    radiusSm: '6px',
    radiusMd: '8px',
    radiusLg: '10px',
    radiusXl: '16px',
  },
  // Surface overlays - for cards, modules, dialogs on dark backgrounds
  surface: {
    overlay: {
      subtle: 'rgba(255, 255, 255, 0.03)', // hover tints
      light: 'rgba(255, 255, 255, 0.08)', // badges, backgrounds
      medium: 'rgba(255, 255, 255, 0.1)', // default borders
      strong: 'rgba(255, 255, 255, 0.2)', // hover borders
      emphasis: 'rgba(255, 255, 255, 0.4)', // selected states
      bright: 'rgba(255, 255, 255, 0.5)', // prominent elements
      max: 'rgba(255, 255, 255, 0.8)', // active indicators
    },
    backdrop: 'rgba(0, 0, 0, 0.4)', // vignettes
    dialogBg: 'rgba(40, 40, 40, 1)', // dialog backgrounds
  },
  transitions: {
    fast: '0.15s ease',
    normal: '0.2s ease',
    slow: '0.3s ease',
  },
  // Menu system - consistent styling across all context menus
  menu: {
    bg: 'rgba(24, 24, 24, 0.98)',
    border: 'rgba(255, 255, 255, 0.12)',
    borderHover: 'rgba(255, 255, 255, 0.2)',
    itemHover: 'rgba(255, 255, 255, 0.08)',
    divider: 'rgba(255, 255, 255, 0.08)',
    radius: '8px',
    shadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
    blur: 'blur(12px)',
    iconSize: 18,
  },
};

declare module '@mui/material/styles/createPalette' {
  interface TypeText {
    hint: string;
  }

  interface PaletteOptions {
    default: PaletteColorOptions;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    cardTitle: React.CSSProperties;
    moduleTitle: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    cardTitle?: React.CSSProperties;
    moduleTitle?: React.CSSProperties;
  }
}
declare module '@mui/material/styles' {
  interface Theme {
    statusColors: {
      pri: StatusStyle;
      sec: StatusStyle;
      currentCard: StatusStyle;
      nextCard: StatusStyle;
      onDeckCard: StatusStyle;
      upcomingCard: StatusStyle;
      doneCard: StatusStyle;
    };
  }

  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    statusColors?: {
      pri?: StatusStyle;
      sec?: StatusStyle;
      currentCard?: StatusStyle;
      nextCard?: StatusStyle;
      onDeckCard?: StatusStyle;
      upcomingCard?: StatusStyle;
      doneCard?: StatusStyle;
    };
  }
}

declare module '@mui/styles' {
  interface DefaultTheme extends Theme {}
}

interface StatusStyle {
  backgroundColor: string;
}

const dummyTheme = createTheme();
const getStatusStyle = (backgroundColor: string): StatusStyle => ({
  backgroundColor,
});

export const getTheme = (darkMode: boolean, useSerifFont: boolean = true) => {
  const titleFontFamily = useSerifFont ? '"Roboto Serif", serif' : '"Roboto", sans-serif';
  const titleFontWeight = useSerifFont ? 300 : 400;

  return createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      background: {
        paper: darkMode ? 'rgb(25, 25, 25)' : '#fff',
        default: darkMode ? 'rgb(15, 15, 15)' : '#fafafa',
      },
      primary: {
        main: '#3B82F6',
      },
      secondary: {
        main: '#f50057',
      },
      text: {
        hint: darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
      },
      default: dummyTheme.palette.augmentColor({
        color: {
          main: '#d0d0d0',
        },
      }),
    },
    typography: {
      cardTitle: {
        fontFamily: titleFontFamily,
        fontWeight: titleFontWeight,
        fontSize: 24,
        textTransform: 'uppercase',
      },
      moduleTitle: {
        fontFamily: titleFontFamily,
        fontWeight: titleFontWeight,
        fontSize: '1rem',
        textTransform: 'uppercase',
      },
    },
    components: {
      MuiButton: {
        defaultProps: {
          // @ts-expect-error
          color: 'default',
        },
        variants: [
          {
            props: { variant: 'outlined' },
            style: {
              color: darkMode ? '#fff' : '#000',
            },
          },
        ],
      },
      MuiTextField: {
        defaultProps: {
          variant: 'standard',
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.grey[700],
            fontSize: 14,
          },
        },
      },
      MuiTabs: {
        defaultProps: {
          indicatorColor: 'secondary',
          textColor: 'inherit',
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minWidth: 160,
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'unset',
          },
        },
      },
    },
    statusColors: {
      pri: getStatusStyle(green[300]),
      sec: getStatusStyle(indigo[300]),
      currentCard: getStatusStyle('#ded135'),
      nextCard: getStatusStyle('#ff7805'),
      onDeckCard: getStatusStyle('#39c6c6'),
      upcomingCard: getStatusStyle(grey[400]),
      doneCard: getStatusStyle(green[600]),
    },
  });
};
